import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import '../styles/pages/_subscriber-management.scss';
import { API_CONFIG, type StudentOptionsResponse } from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import { useAuth } from '../contexts/AuthContext';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
const REQUIRED_HEADERS = ['email', 'password', 'first name', 'last name'];
const OPTIONAL_HEADERS = ['preferred name', 'age', 'provincial', 'municipality', 'city', 'brgy'];
const STUDENT_HEADERS = ['school', 'student number', 'student name', 'student year', 'student course', 'school-email', 'active', 'password', 'age', 'provincial', 'municipality', 'city', 'brgy'];
const EMPLOYEE_HEADERS = ['employee id', 'employee name', 'employee role', 'email', 'password', 'age', 'provincial', 'municipality', 'city', 'brgy'];
const CONSULTANT_HEADERS = ['consultant id', 'consultant name', 'email', 'password', 'age', 'provincial', 'municipality', 'city', 'brgy'];

const TABLE_PAGE_SIZE = 5;

// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div className="subscriber-management__spinner">
    <div className="subscriber-management__spinner-circle"></div>
  </div>
);

interface User {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: 'user' | 'consultant' | 'admin' | 'enterprise';
  status?: 'active' | 'inactive' | 'pending';
  isActive?: boolean;
  createdAt: string;
  lastLogin?: string;
  userType?: string;
  studentNumber?: string | null;
  employeeId?: string | null;
  employeeRole?: string | null;
  consultantId?: string | null;
  course?: string | null;
  yearLevel?: string | null;
}

// API response interface
interface UsersResponse {
  users: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

interface ImportUser {
  email: string;
  password: string;
  profile: { firstName: string; lastName: string; preferredName?: string; age?: number };
  location?: {
    provincial?: string;
    municipality?: string;
    city?: string;
    brgy?: string;
  };
}

interface ImportStudentRow {
  school?: string;
  studentNumber: string;
  studentName: string;
  studentYear?: string;
  studentCourse?: string;
  schoolEmail: string;
  active?: string;
  password: string;
  age?: number;
  provincial?: string;
  municipality?: string;
  city?: string;
  brgy?: string;
}

interface ImportEmployeeRow {
  employeeId: string;
  employeeName: string;
  employeeRole: string;
  email: string;
  password: string;
  age?: number;
  provincial?: string;
  municipality?: string;
  city?: string;
  brgy?: string;
}

interface ImportConsultantRow {
  consultantId: string;
  consultantName: string;
  email: string;
  password: string;
  age?: number;
  provincial?: string;
  municipality?: string;
  city?: string;
  brgy?: string;
}

interface ImportRowError {
  row: number;
  email?: string;
  message: string;
}

type ImportType = 'general' | 'student' | 'employee' | 'consultant';

const STUDENT_REQUIRED_HEADERS = ['student number', 'school-email', 'student name', 'password'];

function detectImportType(headerMap: Record<string, number>, isEnterpriseUsers: boolean): ImportType | null {
  if (isEnterpriseUsers && CONSULTANT_HEADERS.every(h => headerMap[h] !== undefined)) return 'consultant';
  if (EMPLOYEE_HEADERS.every(h => headerMap[h] !== undefined)) return 'employee';
  if (STUDENT_REQUIRED_HEADERS.every(h => headerMap[h] !== undefined)) return 'student';
  if (REQUIRED_HEADERS.every(h => headerMap[h] !== undefined)) return 'general';
  return null;
}

const ConsultantUserTPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isEnterpriseUsers = location.pathname.includes('/panel/enterprise/users');
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [studentOptions, setStudentOptions] = useState<StudentOptionsResponse>({ courses: [], yearByCourse: {} });
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showUserTypeDropdown, setShowUserTypeDropdown] = useState(false);
  const [showAccountStatusDropdown, setShowAccountStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [ownershipFilter, setOwnershipFilter] = useState<'enterpriseOwned' | 'locallyOwned'>('locallyOwned');
  const showEnterpriseTableLayout = isEnterpriseUsers || (!isEnterpriseUsers && ownershipFilter === 'enterpriseOwned');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<UsersResponse['pagination'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState<ImportType>('general');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importUsers, setImportUsers] = useState<(ImportUser | ImportStudentRow | ImportEmployeeRow | ImportConsultantRow)[]>([]);
  const [importValidationErrors, setImportValidationErrors] = useState<ImportRowError[]>([]);
  const [importResult, setImportResult] = useState<{ created: number; updated?: number; failed: number; errors: ImportRowError[] } | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const importAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch student options (courses + yearByCourse) when filtering by Student
  const fetchStudentOptions = useCallback(async () => {
    try {
      const baseUrl = API_CONFIG.ENDPOINTS.USERS_ADMIN_STUDENT_OPTIONS;

      let finalOwnershipFilter = ownershipFilter;
      if (user?.role !== 'Admin') {
        finalOwnershipFilter = (user?.role === 'Enterprise' || user?.createdBy) ? 'enterpriseOwned' : 'locallyOwned';
      }

      const url = isEnterpriseUsers
        ? baseUrl
        : `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}ownership=${finalOwnershipFilter}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to load options');
      const json = await response.json();
      const data = (json.data || {}) as StudentOptionsResponse;
      setStudentOptions({ courses: data.courses || [], yearByCourse: data.yearByCourse || {} });
    } catch {
      setStudentOptions({ courses: [], yearByCourse: {} });
    }
  }, [isEnterpriseUsers, ownershipFilter]);

  useEffect(() => {
    if (userTypeFilter === 'student' && showEnterpriseTableLayout) {
      fetchStudentOptions();
    }
  }, [userTypeFilter, showEnterpriseTableLayout, ownershipFilter, fetchStudentOptions]);

  // Fetch users from API
  const fetchUsers = useCallback(async (
    page: number = 1,
    search: string = '',
    role: string = 'all',
    userType: string = 'all',
    accountStatus: string = 'all',
    dateFromVal: string = '',
    dateToVal: string = '',
    ownership: 'enterpriseOwned' | 'locallyOwned' = 'locallyOwned',
    courseVal: string = 'all',
    yearVal: string = 'all'
  ) => {
    try {
      setIsFetching(true);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: TABLE_PAGE_SIZE.toString()
      });
      if (isEnterpriseUsers) {
        params.append('scope', 'createdBy');
      } else {
        let finalOwnershipFilter = ownership;
        if (user?.role !== 'Admin') {
          // If the user isn't an admin, lock the ownership query to their specific enterprise status
          finalOwnershipFilter = (user?.role === 'Enterprise' || user?.createdBy) ? 'enterpriseOwned' : 'locallyOwned';
        }

        params.append('scope', 'cohort');
        params.append('ownership', finalOwnershipFilter);
      }

      if (search) params.append('search', search);
      if (role !== 'all') params.append('role', role);
      if (userType !== 'all') params.append('userType', userType);
      if (accountStatus !== 'all') params.append('accountStatus', accountStatus);
      if (dateFromVal) params.append('dateFrom', dateFromVal);
      if (dateToVal) params.append('dateTo', dateToVal);
      if (userType === 'student' && courseVal !== 'all') params.append('course', courseVal);
      if (userType === 'student' && yearVal !== 'all') params.append('year', yearVal);


      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_ALL}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Access denied. Only Enterprise can view this list.');
      }
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const fullResponse = await response.json();

      // The backend returns { success: true, data: { users: [...], pagination: {...} } }
      const data = fullResponse.data;

      setUsers(data?.users || []);
      setPagination(data?.pagination || null);

      // Sync currentPage with API response if it differs
      if (data?.pagination?.currentPage && data.pagination.currentPage !== currentPage) {
        setCurrentPage(data.pagination.currentPage);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setNotification({
        type: 'error',
        message: 'Failed to load users. Please try again.'
      });
      // Reset to empty array on error
      setUsers([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [isEnterpriseUsers]);

  // Handle view user details
  const handleViewUser = (userId: string) => {
    navigate(isEnterpriseUsers ? `/panel/enterprise/user/${userId}` : `/panel/consultant/user/${userId}`);
  };

  const totalItems = pagination?.totalUsers || 0;
  const totalPages = pagination?.totalPages || 1;

  // Load initial data
  useEffect(() => {
    fetchUsers(1, '', 'all', 'all', 'all', dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter);
  }, [fetchUsers, dateFrom, dateTo, ownershipFilter]);

  // Handle search and filter changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      setSelectedUsers(new Set());
      fetchUsers(1, searchTerm, roleFilter, userTypeFilter, accountStatusFilter, dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, roleFilter, userTypeFilter, accountStatusFilter, dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter, fetchUsers]);

  // Handle page changes (not triggered by search/filter)
  useEffect(() => {
    if (currentPage >= 1) {
      fetchUsers(currentPage, searchTerm, roleFilter, userTypeFilter, accountStatusFilter, dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter);
    }
  }, [currentPage, searchTerm, roleFilter, userTypeFilter, accountStatusFilter, dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter, fetchUsers]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as Element).closest('.subscriber-management__filter-dropdown')) {
        setShowRoleDropdown(false);
        setShowUserTypeDropdown(false);
        setShowCourseDropdown(false);
        setShowYearDropdown(false);
        setShowAccountStatusDropdown(false);
        setShowDateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Safety timeout: if import is still "in progress" after 95s, force unstick (backstop if fetch/abort never settles)
  useEffect(() => {
    if (!isImporting) return;
    const safetyTimer = setTimeout(() => setIsImporting(false), 95000);
    return () => clearTimeout(safetyTimer);
  }, [isImporting]);

  // Use users directly (API handles pagination)

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (pagination?.hasPrev && currentPage > 1) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Checkbox handlers
  const handleSelectAll = () => {
    const userCount = users?.length || 0;
    if (selectedUsers.size === userCount) {
      // Deselect all
      setSelectedUsers(new Set());
    } else {
      // Select all current page users
      setSelectedUsers(new Set(users?.map(user => user.id) || []));
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  // Export selected users
  const handleExportUsers = () => {
    if (selectedUsers.size === 0) return;

    try {
      const selectedData = users.filter(user => selectedUsers.has(user.id));

      const headers = ['ID', 'User ID', 'Name', 'Email', 'Role', 'User Type', 'Student Number', 'Employee ID', 'Employee Role', 'Created At', 'Last Login', 'Year Level'];
      const csvContent = [
        headers.join(','),
        ...selectedData.map(user => [
          user.id,
          user.userId,
          `"${(user.name || '').replace(/"/g, '""')}"`,
          user.email,
          user.role,
          user.userType || '',
          user.studentNumber || '',
          user.employeeId || '',
          user.employeeRole ? `"${(user.employeeRole || '').replace(/"/g, '""')}"` : '',
          user.createdAt,
          user.lastLogin || '',
          user.yearLevel ?? ''
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setNotification({
        type: 'success',
        message: `Successfully exported ${selectedUsers.size} user${selectedUsers.size > 1 ? 's' : ''} to CSV.`
      });

      // Clear selections after successful operation
      setSelectedUsers(new Set());
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to export users. Please try again.'
      });
    }
  };

  const handleImportClick = () => {
    setShowImportModal(true);
    setImportType('general');
    setImportFile(null);
    setImportUsers([]);
    setImportValidationErrors([]);
    setImportResult(null);
  };

  const normalizeHeader = (h: string) => (h || '').toString().trim().toLowerCase();

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = ev.target?.result;
        if (!data) {
          setImportUsers([]);
          setImportValidationErrors([{ row: 0, message: 'Could not read file.' }]);
          return;
        }
        let rows: unknown[][] = [];
        if (ext === 'csv') {
          const text = typeof data === 'string' ? data : new TextDecoder().decode(data as ArrayBuffer);
          const lines = text.split(/\r?\n/).filter(Boolean);
          rows = lines.map(line => line.split(',').map(c => c.replace(/^"|"$/g, '').trim()));
        } else {
          const wb = XLSX.read(data, { type: ext === 'xlsx' || ext === 'xls' ? 'array' : 'binary' });
          const first = wb.SheetNames[0];
          if (!first) {
            setImportUsers([]);
            setImportValidationErrors([{ row: 0, message: 'No sheet in file.' }]);
            return;
          }
          const sheet = wb.Sheets[first];
          rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as unknown[][];
        }
        if (rows.length < 2) {
          setImportUsers([]);
          setImportValidationErrors([{ row: 1, message: 'File must have a header row and at least one data row.' }]);
          return;
        }
        const headerRow = rows[0] as string[];
        const headerMap: Record<string, number> = {};
        headerRow.forEach((h, i) => {
          headerMap[normalizeHeader(h)] = i;
        });

        const detected = detectImportType(headerMap, isEnterpriseUsers);
        if (detected === null) {
          setImportUsers([]);
          setImportValidationErrors([{ row: 1, message: 'Could not detect import type. Ensure your file has one of the supported header sets.' }]);
          return;
        }
        setImportType(detected);
        const typeToUse = detected;

        if (typeToUse === 'student') {
          const requiredStudent = ['student number', 'school-email', 'student name', 'password'];
          const hasStudentRequired = requiredStudent.every(h => headerMap[h] !== undefined);
          if (!hasStudentRequired) {
            setImportUsers([]);
            setImportValidationErrors([{ row: 1, message: `Student file must include: ${STUDENT_HEADERS.join(', ')}.` }]);
            return;
          }
          const parsed: ImportStudentRow[] = [];
          const errors: ImportRowError[] = [];
          const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i] as unknown[];
            const get = (key: string) => (row[headerMap[key] ?? -1] ?? '').toString().trim();
            const schoolEmail = get('school-email');
            const studentNumber = get('student number');
            const studentName = get('student name');
            const password = get('password');
            const rowNum = i + 1;
            if (!schoolEmail && !studentNumber && !studentName) continue;
            if (!schoolEmail) {
              errors.push({ row: rowNum, message: 'School-Email is required.' });
              continue;
            }
            if (!emailRegex.test(schoolEmail)) {
              errors.push({ row: rowNum, email: schoolEmail, message: 'Valid school email required.' });
              continue;
            }
            if (!studentName) {
              errors.push({ row: rowNum, email: schoolEmail, message: 'Student Name is required.' });
              continue;
            }
            if (!password || password.length < 8 || !PASSWORD_REGEX.test(password)) {
              errors.push({ row: rowNum, email: schoolEmail, message: 'Password required (min 8 chars, upper, lower, number, special).' });
              continue;
            }
            parsed.push({
              school: get('school') || undefined,
              studentNumber: studentNumber || '',
              studentName,
              studentYear: get('student year') || undefined,
              studentCourse: get('student course') || undefined,
              schoolEmail,
              active: get('active') || undefined,
              password,
              age: get('age') ? parseInt(get('age'), 10) : undefined,
              provincial: get('provincial') || undefined,
              municipality: get('municipality') || undefined,
              city: get('city') || undefined,
              brgy: get('brgy') || undefined
            });
          }
          setImportUsers(parsed);
          setImportValidationErrors(errors);
          return;
        }

        if (typeToUse === 'employee') {
          const hasEmployeeRequired = EMPLOYEE_HEADERS.every(h => headerMap[h] !== undefined);
          if (!hasEmployeeRequired) {
            setImportUsers([]);
            setImportValidationErrors([{ row: 1, message: `Employee file must include: ${EMPLOYEE_HEADERS.join(', ')}.` }]);
            return;
          }
          const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
          const parsed: ImportEmployeeRow[] = [];
          const errors: ImportRowError[] = [];
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i] as unknown[];
            const get = (key: string) => (row[headerMap[key] ?? -1] ?? '').toString().trim();
            const employeeId = get('employee id');
            const employeeName = get('employee name');
            const employeeRole = get('employee role');
            const email = get('email').toLowerCase();
            const password = get('password');
            const rowNum = i + 1;
            if (!employeeId && !employeeName && !email && !password) continue;
            if (!employeeId) {
              errors.push({ row: rowNum, message: 'Employee ID is required.' });
              continue;
            }
            if (!email) {
              errors.push({ row: rowNum, message: 'Email is required.' });
              continue;
            }
            if (!emailRegex.test(email)) {
              errors.push({ row: rowNum, email, message: 'Please provide a valid email.' });
              continue;
            }
            if (!password) {
              errors.push({ row: rowNum, email, message: 'Password is required.' });
              continue;
            }
            if (password.length < 8 || !PASSWORD_REGEX.test(password)) {
              errors.push({ row: rowNum, email, message: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.' });
              continue;
            }
            parsed.push({
              employeeId,
              employeeName: employeeName || '',
              employeeRole: employeeRole || '',
              email,
              password,
              age: get('age') ? parseInt(get('age'), 10) : undefined,
              provincial: get('provincial') || undefined,
              municipality: get('municipality') || undefined,
              city: get('city') || undefined,
              brgy: get('brgy') || undefined
            });
          }
          setImportUsers(parsed);
          setImportValidationErrors(errors);
          return;
        }

        if (typeToUse === 'consultant') {
          const hasConsultantRequired = CONSULTANT_HEADERS.every(h => headerMap[h] !== undefined);
          if (!hasConsultantRequired) {
            setImportUsers([]);
            setImportValidationErrors([{ row: 1, message: `Consultant file must include: ${CONSULTANT_HEADERS.join(', ')}.` }]);
            return;
          }
          const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
          const parsed: ImportConsultantRow[] = [];
          const errors: ImportRowError[] = [];
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i] as unknown[];
            const get = (key: string) => (row[headerMap[key] ?? -1] ?? '').toString().trim();
            const consultantId = get('consultant id');
            const consultantName = get('consultant name');
            const email = get('email').toLowerCase();
            const password = get('password');
            const rowNum = i + 1;
            if (!consultantId && !consultantName && !email && !password) continue;
            if (!consultantId) {
              errors.push({ row: rowNum, message: 'Consultant ID is required.' });
              continue;
            }
            if (!email) {
              errors.push({ row: rowNum, message: 'Email is required.' });
              continue;
            }
            if (!emailRegex.test(email)) {
              errors.push({ row: rowNum, email, message: 'Please provide a valid email.' });
              continue;
            }
            if (!password) {
              errors.push({ row: rowNum, email, message: 'Password is required.' });
              continue;
            }
            if (password.length < 8 || !PASSWORD_REGEX.test(password)) {
              errors.push({ row: rowNum, email, message: 'Password must be at least 8 characters with uppercase, lowercase, number, and special character.' });
              continue;
            }
            parsed.push({
              consultantId,
              consultantName: consultantName || '',
              email,
              password,
              age: get('age') ? parseInt(get('age'), 10) : undefined,
              provincial: get('provincial') || undefined,
              municipality: get('municipality') || undefined,
              city: get('city') || undefined,
              brgy: get('brgy') || undefined
            });
          }
          setImportUsers(parsed);
          setImportValidationErrors(errors);
          return;
        }

        const hasRequired = REQUIRED_HEADERS.every(h => headerMap[h] !== undefined);
        if (!hasRequired) {
          setImportUsers([]);
          setImportValidationErrors([{ row: 1, message: `Header row must include: ${REQUIRED_HEADERS.join(', ')}. Optional: ${OPTIONAL_HEADERS.join(', ')}.` }]);
          return;
        }
        const emailIdx = headerMap['email'];
        const passwordIdx = headerMap['password'];
        const firstNameIdx = headerMap['first name'];
        const lastNameIdx = headerMap['last name'];
        const preferredNameIdx = headerMap['preferred name'];
        const parsed: ImportUser[] = [];
        const errors: ImportRowError[] = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i] as unknown[];
          const email = (row[emailIdx] ?? '').toString().trim();
          const password = (row[passwordIdx] ?? '').toString().trim();
          const firstName = (row[firstNameIdx] ?? '').toString().trim();
          const lastName = (row[lastNameIdx] ?? '').toString().trim();
          const preferredName = (row[preferredNameIdx] ?? '').toString().trim() || undefined;
          const rowNum = i + 1;
          if (!email && !password && !firstName && !lastName) continue;
          if (!email) {
            errors.push({ row: rowNum, email, message: 'Email is required.' });
            continue;
          }
          const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
          if (!emailRegex.test(email)) {
            errors.push({ row: rowNum, email, message: 'Please provide a valid email.' });
            continue;
          }
          if (!password) {
            errors.push({ row: rowNum, email, message: 'Password is required.' });
            continue;
          }
          if (password.length < 8) {
            errors.push({ row: rowNum, email, message: 'Password must be at least 8 characters long.' });
            continue;
          }
          if (!PASSWORD_REGEX.test(password)) {
            errors.push({ row: rowNum, email, message: 'Password must contain at least one uppercase, one lowercase, one number, and one special character (@$!%*?&).' });
            continue;
          }
          if (!firstName) {
            errors.push({ row: rowNum, email, message: 'First name is required.' });
            continue;
          }
          if (!lastName) {
            errors.push({ row: rowNum, email, message: 'Last name is required.' });
            continue;
          }
          parsed.push({
            email,
            password,
            profile: {
              firstName,
              lastName,
              preferredName,
              age: (row[headerMap['age']] ? parseInt((row[headerMap['age']] ?? '').toString().trim(), 10) : undefined)
            },
            location: {
              provincial: (row[headerMap['provincial']] ?? '').toString().trim() || undefined,
              municipality: (row[headerMap['municipality']] ?? '').toString().trim() || undefined,
              city: (row[headerMap['city']] ?? '').toString().trim() || undefined,
              brgy: (row[headerMap['brgy']] ?? '').toString().trim() || undefined
            }
          });
        }
        setImportUsers(parsed);
        setImportValidationErrors(errors);
      } catch (err) {
        setImportUsers([]);
        setImportValidationErrors([{ row: 0, message: (err as Error).message || 'Failed to parse file.' }]);
      }
    };
    if (ext === 'csv') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleDownloadTemplate = (type: ImportType) => {
    setImportFile(null);
    setImportUsers([]);
    setImportValidationErrors([]);
    if (type === 'student') {
      const headers = ['School', 'Student Number', 'Student Name', 'Student Year', 'Student Course', 'School-Email', 'Active', 'Password', 'Age', 'Provincial', 'Municipality', 'City', 'Brgy'];
      const example = ['Acme School', 'S001', 'Jane Doe', '2nd-year', 'BS Computer Science', 'jane@school.edu', 'Active', 'SecurePass1!', '20', 'Metro Manila', 'Pateros', 'Taguig', 'Fort Bonifacio'];
      const csv = [headers.join(','), example.join(',')].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'students_import_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === 'employee') {
      const headers = ['Employee ID', 'Employee Name', 'Employee Role', 'Email', 'Password', 'Age', 'Provincial', 'Municipality', 'City', 'Brgy'];
      const example = ['E001', 'John Smith', 'Instructor', 'john@company.com', 'SecurePass1!', '30', 'Cebu', 'Minglanilla', 'Cebu City', 'Lahug'];
      const csv = [headers.join(','), example.join(',')].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'employees_import_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (type === 'consultant') {
      const headers = ['Consultant ID', 'Consultant Name', 'Email', 'Password', 'Age', 'Provincial', 'Municipality', 'City', 'Brgy'];
      const example = ['C001', 'Jane Consultant', 'jane@example.com', 'SecurePass1!', '35', 'Metro Manila', 'Makati', 'Makati City', 'Poblacion'];
      const csv = [headers.join(','), example.join(',')].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'consultants_import_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const headers = ['Email', 'Password', 'First Name', 'Last Name', 'Preferred Name', 'Age', 'Provincial', 'Municipality', 'City', 'Brgy'];
      const example = ['jane@example.com', 'SecurePass1!', 'Jane', 'Doe', 'Janie', '25', 'Metro Manila', 'Quezon', 'Quezon City', 'Diliman'];
      const csv = [headers.join(','), example.join(',')].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'users_import_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleImportSubmit = async () => {
    if (importUsers.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setNotification({ type: 'error', message: 'Authentication required.' });
      return;
    }
    importAbortControllerRef.current?.abort();
    setIsImporting(true);
    setImportResult(null);
    const controller = new AbortController();
    importAbortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      const body = importType === 'general'
        ? { users: importUsers }
        : { importType, users: importUsers };
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS_ADMIN_IMPORT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      let data: { success?: boolean; message?: string; created?: number; updated?: number; failed?: number; errors?: ImportRowError[] };
      try {
        data = await response.json();
      } catch {
        setNotification({ type: 'error', message: 'Invalid response from server. Please try again.' });
        setImportResult({ created: 0, failed: importUsers.length, errors: [] });
        return;
      }
      if (!response.ok) {
        setImportResult({
          created: data.created ?? 0,
          failed: data.failed ?? importUsers.length,
          errors: (data.errors || []).map((e: ImportRowError) => ({ row: e.row, email: e.email, message: e.message }))
        });
        setNotification({ type: 'error', message: data.message || 'Import failed.' });
        return;
      }
      setImportResult({
        created: data.created ?? 0,
        updated: data.updated ?? 0,
        failed: data.failed ?? 0,
        errors: (data.errors || []).map((e: ImportRowError) => ({ row: e.row, email: e.email, message: e.message }))
      });
      const msg = data.updated != null
        ? `${data.created} created, ${data.updated} updated, ${data.failed} failed.`
        : `${data.created} user(s) created, ${data.failed} failed.`;
      setNotification({ type: 'success', message: data.message || `Import complete. ${msg}` });
      setIsImporting(false);
      setShowImportModal(false);
      setImportFile(null);
      setImportUsers([]);
      setImportValidationErrors([]);
      setImportResult(null);
      fetchUsers(currentPage, searchTerm, roleFilter, userTypeFilter, accountStatusFilter, dateFrom, dateTo, ownershipFilter, courseFilter, yearFilter);
    } catch (err) {
      clearTimeout(timeoutId);
      const isAbort = err instanceof Error && err.name === 'AbortError';
      setNotification({
        type: 'error',
        message: isAbort ? 'The server did not respond in time. Please check that the backend is running and try again.' : (err as Error).message || 'Import failed.'
      });
      setImportResult({ created: 0, failed: importUsers.length, errors: [] });
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportCancel = () => {
    importAbortControllerRef.current?.abort();
    setIsImporting(false);
    setShowImportModal(false);
    setImportFile(null);
    setImportUsers([]);
    setImportValidationErrors([]);
    setImportResult(null);
  };

  return (
    <div className="subscriber-management">
      <main className="subscriber-management__main">
        <div className="subscriber-management__container">
          {/* Header Section */}
          <div className="subscriber-management__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="subscriber-management__title-section">
              <h1 className="subscriber-management__title">
                {isEnterpriseUsers ? 'Enterprise Users' : 'UserT - User Management'}
              </h1>
              <p className="subscriber-management__subtitle">
                {isEnterpriseUsers ? 'Manage users created by your enterprise. Import students or employees via Excel.' : 'Manage, filter, and monitor all users in the system.'}
              </p>
            </div>

            {!isEnterpriseUsers && (
              <div className="consultant-vulnerabilities__ownership-toggle" aria-label="Ownership filter">
                {user?.role === 'Admin' ? (
                  <>
                    <button
                      type="button"
                      className={`subscriber-management__ownership-btn ${ownershipFilter === 'locallyOwned' ? 'subscriber-management__ownership-btn--active' : ''}`}
                      onClick={() => setOwnershipFilter('locallyOwned')}
                    >
                      Locally Owned
                    </button>
                    <button
                      type="button"
                      className={`subscriber-management__ownership-btn ${ownershipFilter === 'enterpriseOwned' ? 'subscriber-management__ownership-btn--active' : ''}`}
                      onClick={() => setOwnershipFilter('enterpriseOwned')}
                    >
                      Enterprise Owned
                    </button>
                  </>
                ) : (
                  <div className={`consultant-vulnerabilities__ownership-badge consultant-vulnerabilities__ownership-badge--${(user?.role === 'Enterprise' || user?.createdBy) ? 'enterprise' : 'personal'}`}>
                    {(user?.role === 'Enterprise' || user?.createdBy) ? 'Enterprise' : 'Personal'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Filters Section */}
          <div className="subscriber-management__filters">
            <div className="subscriber-management__search-container">
              <div className="subscriber-management__search-input-wrapper">
                <span className="material-symbols-outlined subscriber-management__search-input-wrapper__search-icon">search</span>
                <input
                  type="text"
                  className="subscriber-management__search-input-wrapper__search-input"
                  placeholder="Search by name, email, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="subscriber-management__search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                )}
              </div>
            </div>
            <div className="subscriber-management__filter-dropdown">
              <button
                className={`subscriber-management__filter-btn ${roleFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                onClick={() => setShowRoleDropdown(!showRoleDropdown)}
              >
                <div className="subscriber-management__filter-content">
                  <span className="material-symbols-outlined">person</span>
                  <p>Role: {roleFilter === 'all' ? 'All' : roleFilter.charAt(0).toUpperCase() + roleFilter.slice(1)}</p>
                </div>
                <span className="material-symbols-outlined">expand_more</span>
              </button>
              {showRoleDropdown && (
                <div className="subscriber-management__dropdown-menu">
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setRoleFilter('all'); setShowRoleDropdown(false); }}
                  >
                    All
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setRoleFilter('user'); setShowRoleDropdown(false); }}
                  >
                    User
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setRoleFilter('consultant'); setShowRoleDropdown(false); }}
                  >
                    Consultant
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setRoleFilter('admin'); setShowRoleDropdown(false); }}
                  >
                    Admin
                  </button>
                </div>
              )}
            </div>
            {showEnterpriseTableLayout && (
              <div className="subscriber-management__filter-dropdown">
                <button
                  className={`subscriber-management__filter-btn ${userTypeFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                  onClick={() => setShowUserTypeDropdown(!showUserTypeDropdown)}
                >
                  <div className="subscriber-management__filter-content">
                    <span className="material-symbols-outlined">category</span>
                    <p>User Type: {userTypeFilter === 'all' ? 'All' : userTypeFilter.charAt(0).toUpperCase() + userTypeFilter.slice(1)}</p>
                  </div>
                  <span className="material-symbols-outlined">expand_more</span>
                </button>
                {showUserTypeDropdown && (
                  <div className="subscriber-management__dropdown-menu">
                    <button
                      className="subscriber-management__dropdown-item"
                      onClick={() => { setUserTypeFilter('all'); setCourseFilter('all'); setYearFilter('all'); setShowUserTypeDropdown(false); }}
                    >
                      All
                    </button>
                    <button
                      className="subscriber-management__dropdown-item"
                      onClick={() => { setUserTypeFilter('student'); setShowUserTypeDropdown(false); }}
                    >
                      Student
                    </button>
                    <button
                      className="subscriber-management__dropdown-item"
                      onClick={() => { setUserTypeFilter('employee'); setCourseFilter('all'); setYearFilter('all'); setShowUserTypeDropdown(false); }}
                    >
                      Employee
                    </button>
                    <button
                      className="subscriber-management__dropdown-item"
                      onClick={() => { setUserTypeFilter('consultant'); setCourseFilter('all'); setYearFilter('all'); setShowUserTypeDropdown(false); }}
                    >
                      Consultant
                    </button>
                  </div>
                )}
              </div>
            )}
            {showEnterpriseTableLayout && userTypeFilter === 'student' && (
              <>
                <div className="subscriber-management__filter-dropdown">
                  <button
                    className={`subscriber-management__filter-btn ${courseFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                    onClick={() => setShowCourseDropdown(!showCourseDropdown)}
                  >
                    <div className="subscriber-management__filter-content">
                      <span className="material-symbols-outlined">school</span>
                      <p>Course: {courseFilter === 'all' ? 'All course' : courseFilter}</p>
                    </div>
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  {showCourseDropdown && (
                    <div className="subscriber-management__dropdown-menu">
                      <button
                        className="subscriber-management__dropdown-item"
                        onClick={() => {
                          setCourseFilter('all');
                          setYearFilter('all');
                          setShowCourseDropdown(false);
                        }}
                      >
                        All course
                      </button>
                      {studentOptions.courses.map((c) => (
                        <button
                          key={c}
                          className="subscriber-management__dropdown-item"
                          onClick={() => {
                            setCourseFilter(c);
                            const years = studentOptions.yearByCourse[c] || [];
                            if (yearFilter !== 'all' && years.length > 0 && !years.includes(yearFilter)) {
                              setYearFilter('all');
                            }
                            setShowCourseDropdown(false);
                          }}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="subscriber-management__filter-dropdown">
                  <button
                    className={`subscriber-management__filter-btn ${yearFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                    onClick={() => setShowYearDropdown(!showYearDropdown)}
                  >
                    <div className="subscriber-management__filter-content">
                      <span className="material-symbols-outlined">calendar_today</span>
                      <p>Year: {yearFilter === 'all' ? 'All year' : yearFilter}</p>
                    </div>
                    <span className="material-symbols-outlined">expand_more</span>
                  </button>
                  {showYearDropdown && (
                    <div className="subscriber-management__dropdown-menu">
                      <button
                        className="subscriber-management__dropdown-item"
                        onClick={() => { setYearFilter('all'); setShowYearDropdown(false); }}
                      >
                        All year
                      </button>
                      {(courseFilter === 'all' ? [] : (studentOptions.yearByCourse[courseFilter] || [])).map((y) => (
                        <button
                          key={y}
                          className="subscriber-management__dropdown-item"
                          onClick={() => { setYearFilter(y); setShowYearDropdown(false); }}
                        >
                          {y}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <div className="subscriber-management__filter-dropdown">
              <button
                className={`subscriber-management__filter-btn ${accountStatusFilter !== 'all' ? 'subscriber-management__filter-btn--active' : ''}`}
                onClick={() => setShowAccountStatusDropdown(!showAccountStatusDropdown)}
              >
                <div className="subscriber-management__filter-content">
                  <span className="material-symbols-outlined">toggle_on</span>
                  <p>Account: {accountStatusFilter === 'all' ? 'All' : accountStatusFilter === 'active' ? 'Active' : 'Inactive'}</p>
                </div>
                <span className="material-symbols-outlined">expand_more</span>
              </button>
              {showAccountStatusDropdown && (
                <div className="subscriber-management__dropdown-menu">
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setAccountStatusFilter('all'); setShowAccountStatusDropdown(false); }}
                  >
                    All
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setAccountStatusFilter('active'); setShowAccountStatusDropdown(false); }}
                  >
                    Active
                  </button>
                  <button
                    className="subscriber-management__dropdown-item"
                    onClick={() => { setAccountStatusFilter('inactive'); setShowAccountStatusDropdown(false); }}
                  >
                    Inactive
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table Section */}
          <div className="subscriber-management__table-container">
            {/* Table Header */}
            <div className="subscriber-management__table-header">
              <p className="subscriber-management__table-info">Showing {Math.max(1, ((currentPage - 1) * TABLE_PAGE_SIZE) + 1)}-{Math.max(1, ((currentPage - 1) * TABLE_PAGE_SIZE) + (users?.length || 0))} of {totalItems} users</p>
              <div className="subscriber-management__table-actions">
                <button
                  type="button"
                  className="subscriber-management__action-btn"
                  onClick={handleImportClick}
                  title="Import users from Excel"
                >
                  <span className="material-symbols-outlined">upload_file</span>
                  <span className="sr-only">Import</span>
                </button>
                <button
                  className={`subscriber-management__action-btn ${selectedUsers.size === 0 ? 'subscriber-management__action-btn--disabled' : ''}`}
                  disabled={selectedUsers.size === 0}
                  onClick={handleExportUsers}
                  title="Export selected users"
                >
                  <span className="material-symbols-outlined">download</span>
                  <span className="sr-only">Export</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="subscriber-management__table-wrapper">
              {isFetching && (
                <div className="subscriber-management__loading-overlay">
                  <LoadingSpinner />
                  <span>Loading users...</span>
                </div>
              )}
              <table className="subscriber-management__table">
                <thead className="subscriber-management__table-head">
                  <tr>
                    <th className="subscriber-management__table-th subscriber-management__table-th--checkbox" scope="col">
                      <div className="subscriber-management__checkbox-wrapper">
                        <input
                          className="subscriber-management__checkbox"
                          id="checkbox-all"
                          type="checkbox"
                          checked={(users?.length || 0) > 0 && selectedUsers.size === (users?.length || 0)}
                          onChange={handleSelectAll}
                          disabled={isFetching}
                        />
                        <label className="sr-only" htmlFor="checkbox-all">Select all users</label>
                      </div>
                    </th>
                    <th className="subscriber-management__table-th" scope="col">User</th>
                    <th className="subscriber-management__table-th" scope="col">Account</th>
                    {showEnterpriseTableLayout && (
                      <>
                        <th className="subscriber-management__table-th" scope="col">User Type</th>
                        <th className="subscriber-management__table-th" scope="col">ID</th>
                      </>
                    )}
                    <th className="subscriber-management__table-th" scope="col">Role</th>
                    <th className="subscriber-management__table-th" scope="col">Course</th>
                    <th className="subscriber-management__table-th" scope="col">Year Level</th>
                    <th className="subscriber-management__table-th" scope="col">Last Login</th>
                    <th className="subscriber-management__table-th" scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    // Loading skeleton rows
                    Array.from({ length: TABLE_PAGE_SIZE }, (_, index) => (
                      <tr key={`loading-${index}`} className="subscriber-management__table-row">
                        <td className="subscriber-management__table-td subscriber-management__table-td--checkbox">
                          <div className="subscriber-management__skeleton"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__user-info">
                            <div className="subscriber-management__skeleton subscriber-management__skeleton--name"></div>
                            <div className="subscriber-management__skeleton subscriber-management__skeleton--email"></div>
                          </div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        {showEnterpriseTableLayout && (
                          <>
                            <td className="subscriber-management__table-td">
                              <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                            </td>
                            <td className="subscriber-management__table-td">
                              <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                            </td>
                          </>
                        )}
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                        <td className="subscriber-management__table-td">
                          <div className="subscriber-management__skeleton subscriber-management__skeleton--small"></div>
                        </td>
                      </tr>
                    ))
                  ) : (users?.length || 0) === 0 ? (
                    <tr className="subscriber-management__table-row">
                      <td colSpan={showEnterpriseTableLayout ? 10 : 9} className="subscriber-management__table-td subscriber-management__table-td--empty">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    (users || []).map((user) => (
                      <tr key={user.id} className="subscriber-management__table-row" onClick={() => handleViewUser(user.id)}>
                        <td className="subscriber-management__table-td subscriber-management__table-td--checkbox">
                          <div className="subscriber-management__checkbox-wrapper">
                            <input
                              className="subscriber-management__checkbox"
                              id={`checkbox-${user.id}`}
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={() => handleSelectUser(user.id)}
                            />
                            <label className="sr-only" htmlFor={`checkbox-${user.id}`}>Select {user.name}</label>
                          </div>
                        </td>
                        <th className="subscriber-management__table-td subscriber-management__table-td--user" scope="row">
                          <div className="subscriber-management__user-info">
                            <div className="subscriber-management__user-name">{user.name}</div>
                            <div className="subscriber-management__user-email">{user.email}</div>
                          </div>
                        </th>
                        <td className="subscriber-management__table-td">
                          <span className={`subscriber-management__account-badge subscriber-management__account-badge--${user.isActive === false ? 'inactive' : 'active'}`}>
                            {user.isActive === false ? 'Inactive' : 'Active'}
                          </span>
                        </td>
                        {showEnterpriseTableLayout && (
                          <>
                            <td className="subscriber-management__table-td">
                              <span className="subscriber-management__role-badge subscriber-management__role-badge--user">
                                {user.userType ? (user.userType.charAt(0).toUpperCase() + user.userType.slice(1)) : '—'}
                              </span>
                            </td>
                            <td className="subscriber-management__table-td">
                              {user.userType === 'student' ? (user.studentNumber || '—') : user.userType === 'employee' ? (user.employeeId || '—') : user.userType === 'consultant' ? (user.consultantId || '—') : '—'}
                            </td>
                          </>
                        )}
                        <td className="subscriber-management__table-td">
                          <span className={`subscriber-management__role-badge subscriber-management__role-badge--${user.role}`}>
                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                          </span>
                        </td>
                        <td className="subscriber-management__table-td">{user.course || '\u00A0'}</td>
                        <td className="subscriber-management__table-td">{user.yearLevel || '\u00A0'}</td>
                        <td className="subscriber-management__table-td">{user.lastLogin ? formatDate(user.lastLogin) : '\u00A0'}</td>
                        <td className="subscriber-management__table-td">
                          <button
                            className="subscriber-management__action-link"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewUser(user.id);
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <nav aria-label="Table navigation" className="subscriber-management__pagination">
              <span className="subscriber-management__pagination-info">
                Showing <span className="subscriber-management__pagination-highlight">{Math.max(1, ((currentPage - 1) * TABLE_PAGE_SIZE) + 1)}-{Math.max(1, ((currentPage - 1) * TABLE_PAGE_SIZE) + (users?.length || 0))}</span> of <span className="subscriber-management__pagination-highlight">{totalItems}</span>
              </span>
              <ul className="subscriber-management__pagination-list">
                <li>
                  <button
                    className="subscriber-management__pagination-link subscriber-management__pagination-link--previous"
                    onClick={handlePrevPage}
                    disabled={!pagination?.hasPrev}
                  >
                    Previous
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <li key={page}>
                    <button
                      className={`subscriber-management__pagination-link ${page === currentPage ? 'subscriber-management__pagination-link--active' : ''
                        }`}
                      onClick={() => handlePageChange(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    className="subscriber-management__pagination-link"
                    onClick={handleNextPage}
                    disabled={!pagination?.hasNext}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </main>

      {/* Import users modal */}
      {showImportModal && (
        <div className="subscriber-management__import-overlay" onClick={handleImportCancel}>
          <div className="subscriber-management__import-modal" onClick={e => e.stopPropagation()}>
            <div className="subscriber-management__import-header">
              <h2 className="subscriber-management__import-title">Import users from Excel</h2>
              <button type="button" className="subscriber-management__import-close" onClick={handleImportCancel} aria-label="Close">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="subscriber-management__import-format">
              <p className="subscriber-management__import-format-intro">Download a template, then upload your file. Format is detected automatically from your file headers.</p>
              <h3 className="subscriber-management__import-format-title">Supported formats</h3>
              {importFile && importUsers.length > 0 ? (
                <>
                  <p className="subscriber-management__import-detected">Detected: <strong>{importType.charAt(0).toUpperCase() + importType.slice(1)}</strong></p>
                  {importType === 'general' && (
                    <ul className="subscriber-management__import-format-list">
                      <li>Header: <strong>Email</strong>, <strong>Password</strong>, <strong>First Name</strong>, <strong>Last Name</strong>. Optional: <strong>Preferred Name</strong>.</li>
                    </ul>
                  )}
                  {importType === 'student' && (
                    <ul className="subscriber-management__import-format-list">
                      <li>Header: <strong>School</strong>, <strong>Student Number</strong>, <strong>Student Name</strong>, <strong>School-Email</strong>, <strong>Password</strong>, etc.</li>
                    </ul>
                  )}
                  {importType === 'employee' && (
                    <ul className="subscriber-management__import-format-list">
                      <li>Header: <strong>Employee ID</strong>, <strong>Employee Name</strong>, <strong>Employee Role</strong>.</li>
                    </ul>
                  )}
                  {importType === 'consultant' && (
                    <ul className="subscriber-management__import-format-list">
                      <li>Header: <strong>Consultant ID</strong>, <strong>Consultant Name</strong>.</li>
                    </ul>
                  )}
                </>
              ) : (
                <ul className="subscriber-management__import-format-list">
                  <li><strong>General:</strong> Email, Password, First Name, Last Name (optional: Preferred Name).</li>
                  <li><strong>Student:</strong> School, Student Number, Student Name, School-Email, Active, Password.</li>
                  <li><strong>Employee:</strong> Employee ID, Employee Name, Employee Role.</li>
                  {isEnterpriseUsers && <li><strong>Consultant:</strong> Consultant ID, Consultant Name.</li>}
                  <li>Accepted: .xlsx, .xls, .csv</li>
                </ul>
              )}
              <div className="subscriber-management__import-template-btns">
                <button type="button" className="subscriber-management__import-template-btn" onClick={() => handleDownloadTemplate('general')}>
                  General template
                </button>
                <button type="button" className="subscriber-management__import-template-btn" onClick={() => handleDownloadTemplate('student')}>
                  Student template
                </button>
                <button type="button" className="subscriber-management__import-template-btn" onClick={() => handleDownloadTemplate('employee')}>
                  Employee template
                </button>
                {isEnterpriseUsers && (
                  <button type="button" className="subscriber-management__import-template-btn" onClick={() => handleDownloadTemplate('consultant')}>
                    Consultant template
                  </button>
                )}
              </div>
            </div>
            <div className="subscriber-management__import-file">
              <label className="subscriber-management__import-file-label">
                <span className="material-symbols-outlined">folder_open</span>
                Choose file
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="subscriber-management__import-file-input"
                  onChange={handleImportFileChange}
                />
              </label>
              {importFile && <p className="subscriber-management__import-file-name">{importFile.name}</p>}
              {importUsers.length > 0 && (
                <p className="subscriber-management__import-preview">Detected: <strong>{importType.charAt(0).toUpperCase() + importType.slice(1)}</strong>. {importUsers.length} valid row(s) ready to import.</p>
              )}
              {importValidationErrors.length > 0 && (
                <ul className="subscriber-management__import-errors">
                  {importValidationErrors.slice(0, 10).map((e, i) => (
                    <li key={i}>Row {e.row}: {e.message}</li>
                  ))}
                  {importValidationErrors.length > 10 && <li>… and {importValidationErrors.length - 10} more</li>}
                </ul>
              )}
              {importResult && (
                <div className="subscriber-management__import-result">
                  <p>
                    {importResult.created} created
                    {importResult.updated != null && importResult.updated > 0 ? `, ${importResult.updated} updated` : ''}
                    , {importResult.failed} failed.
                  </p>
                  {importResult.errors.length > 0 && (
                    <ul className="subscriber-management__import-errors">
                      {importResult.errors.slice(0, 10).map((e, i) => (
                        <li key={i}>Row {e.row}: {e.message}</li>
                      ))}
                      {importResult.errors.length > 10 && <li>… and {importResult.errors.length - 10} more</li>}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="subscriber-management__import-actions">
              <button type="button" className="subscriber-management__import-cancel-btn" onClick={handleImportCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="subscriber-management__import-submit-btn"
                disabled={importUsers.length === 0 || isImporting}
                onClick={handleImportSubmit}
              >
                {isImporting ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consultant users: ownership toggle (bottom right) - Admin only here because badge was moved to header */}
      {!isEnterpriseUsers && user?.role === 'Admin' && (
        <div className="subscriber-management__ownership-toggle" aria-label="Ownership filter">
          <button
            type="button"
            className={`subscriber-management__ownership-btn ${ownershipFilter === 'locallyOwned' ? 'subscriber-management__ownership-btn--active' : ''}`}
            onClick={() => setOwnershipFilter('locallyOwned')}
          >
            Locally Owned
          </button>
          <button
            type="button"
            className={`subscriber-management__ownership-btn ${ownershipFilter === 'enterpriseOwned' ? 'subscriber-management__ownership-btn--active' : ''}`}
            onClick={() => setOwnershipFilter('enterpriseOwned')}
          >
            Enterprise Owned
          </button>
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <div className={`subscriber-management__notification subscriber-management__notification--${notification.type}`}>
          <span className="material-symbols-outlined">
            {notification.type === 'success' ? 'check_circle' :
              notification.type === 'error' ? 'error' : 'info'}
          </span>
          <span>{notification.message}</span>
          <button
            className="subscriber-management__notification-close"
            onClick={() => setNotification(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      )}

    </div>
  );
};

export default ConsultantUserTPage;